import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPaginatedAssetsForSearchAndSelect } from '../../../gqlActions';
import {
  AssetsListGroup,
  GetPaginatedAssetsForSearchAndSelectQuery,
  GetPaginatedAssetsForSearchAndSelectQuery$variables,
} from '@bts-web/data-layer/server';
import { debounce } from '@bts-web/utils-functions';
import { MINIMUM_SEARCH_TERM_LENGTH } from '../../../../config';
import {
  AssetSelectDrawerItemEntry,
  AssetSelectHookResult,
  extractAssetsFromGeneralFetchAllResponse,
  getPaginatedAssetsForSelectInitialQueryParams,
} from './utils';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';
import { datadogErrorHelper } from '../../../utils/datadogErrorHelper';

const DEBOUNCE_DELAY_AMOUNT_MS = 500;

export const ASSETS_SELECT_ITEMS_PER_PAGE = 10;

export const EmptyPaginatedAssetsForSelectResponse = {
  assetGroups: [],
  assets: {
    edges: [],
    totalCount: 0,
    pageInfo: {
      hasNextPage: false,
      endCursor: null,
      hasPreviousPage: false,
      startCursor: null,
    },
  },
} satisfies GetPaginatedAssetsForSearchAndSelectQuery['response'];

export const useAllAssetSelectSearchAndInfiniteScroll =
  (): AssetSelectHookResult => {
    const [groups, setGroups] = useState<AssetsListGroup[] | undefined>(
      undefined,
    );

    const [fullAssetData, setFullAssetData] = useState<
      GetPaginatedAssetsForSearchAndSelectQuery['response'] | null
    >(null);

    const { assetGroups } = fullAssetData ?? { assetGroups: [] };

    const [assetFilterSearchValue, setAssetFilterSearchValue] = useState('');

    const [isLoading, setIsLoading] = useState(true);

    // Extract processed data from the full asset data
    const { assets, canLoadMore, endCursorId } = useMemo(() => {
      let processed: AssetSelectDrawerItemEntry[] = [];

      if (fullAssetData) {
        processed = extractAssetsFromGeneralFetchAllResponse({
          responseData: fullAssetData,
        });

        const uniqueAssets = new Map<string, AssetSelectDrawerItemEntry>();

        processed.forEach((asset) => {
          if (asset.id) {
            uniqueAssets.set(asset.id, asset);
          }
        });

        processed = Array.from(uniqueAssets.values());
      }

      return {
        assets: processed,
        canLoadMore: fullAssetData?.assets?.pageInfo?.hasNextPage ?? false,
        endCursorId: fullAssetData?.assets?.pageInfo?.endCursor ?? null,
      };
    }, [fullAssetData]);

    const fetchAssets = useCallback(
      async (
        params: GetPaginatedAssetsForSearchAndSelectQuery$variables['input'],
      ) => {
        try {
          const queryParams = {
            ...getPaginatedAssetsForSelectInitialQueryParams,
            ...params,
            ...(groups ? { groups } : {}),
          };

          const { data } =
            await getPaginatedAssetsForSearchAndSelect(queryParams);

          return data || EmptyPaginatedAssetsForSelectResponse;
        } catch (error) {
          console.error('Error fetching assets:', error);

          return EmptyPaginatedAssetsForSelectResponse;
        }
      },
      [groups],
    );

    const debouncedSearchRef = useRef(
      debounce(async (input: string) => {
        const sanitizedInput = input.trim();

        setIsLoading(true);

        try {
          let data;

          if (
            sanitizedInput.length === 0 ||
            sanitizedInput.length < MINIMUM_SEARCH_TERM_LENGTH
          ) {
            data = await fetchAssets({});
          } else {
            data = await fetchAssets({ searchTerm: sanitizedInput });
          }

          setFullAssetData(data);
        } finally {
          setIsLoading(false);
        }
      }, DEBOUNCE_DELAY_AMOUNT_MS),
    );

    const loadMore = useCallback(async () => {
      if (!canLoadMore || !endCursorId) return;

      try {
        const responseData = await fetchAssets({ after: endCursorId });

        setFullAssetData((prevData) => {
          if (!prevData || !responseData) return prevData;

          const existingIds = new Set<string>();

          prevData.assets?.edges?.forEach((edge) => {
            if (edge?.node?.id) {
              existingIds.add(edge.node.id);
            }
          });

          const uniqueNewEdges =
            responseData.assets?.edges?.filter(
              (edge) => edge?.node?.id && !existingIds.has(edge.node.id),
            ) || [];

          return {
            assetGroups:
              responseData.assetGroups?.length > 0
                ? responseData.assetGroups
                : prevData.assetGroups,
            assets: {
              edges: [...(prevData.assets?.edges || []), ...uniqueNewEdges],
              totalCount: responseData.assets?.totalCount ?? 0,
              pageInfo:
                responseData.assets?.pageInfo ??
                EmptyPaginatedAssetsForSelectResponse.assets.pageInfo,
            },
          };
        });
      } catch (error) {
        datadogErrorHelper({
          errorMessage: 'Error fetching more assets: ' + JSON.stringify(error),
          errorSeverity: DatadogErrorLevels.HIGH,
        });
      }
    }, [canLoadMore, endCursorId, fetchAssets]);

    const onInputChange = useCallback((newValue: string) => {
      setAssetFilterSearchValue(newValue);

      setIsLoading(true);

      debouncedSearchRef.current(newValue);
    }, []);

    const clearSearchParams = useCallback(() => {
      setAssetFilterSearchValue('');

      setIsLoading(true);

      debouncedSearchRef.current('');
    }, []);

    useEffect(() => {
      const initialFetch = async () => {
        setIsLoading(true);

        const data = await fetchAssets({});

        if (data.assets) {
          setFullAssetData(data);

          setIsLoading(false);
        }
      };

      initialFetch();
    }, [fetchAssets]);

    return {
      assetGroups,
      groups,
      setGroups,
      assets,
      assetFilterSearchValue,
      onInputChange,
      loadMore,
      clearSearchParams,
      isLoading,
      canLoadMore,
    };
  };
